
// packages/components/src/agent-management/SystemPromptCard.tsx
import React, { useState, useEffect } from 'react';
import { Card, Button, TextareaField, IconButton, Modal, Spinner, Alert, Tooltip } from '@dopomogai/ui';
import { useAgentPrompts } from '@dopomogai/agent-kit';
import type { PromptResponse } from '@dopomogai/types';
import { Save, History, CheckCircle, Brain, Lock } from 'lucide-react';
import { toast } from '@dopomogai/ui';

interface SystemPromptCardProps {
  agentId: string;
  readOnly?: boolean;
}

export const SystemPromptCard: React.FC<SystemPromptCardProps> = ({ agentId, readOnly = false }) => {
  const {
    activePrompt,
    prompts,
    createAndActivatePrompt,
    setActivePrompt,
    updateActivePromptContent,
    isLoading,
    error,
  } = useAgentPrompts(agentId);

  const [promptContent, setPromptContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  useEffect(() => {
    setPromptContent(activePrompt?.content || '');
  }, [activePrompt]);

  const trimmedContent = promptContent.trim();
  const originalTrimmed = (activePrompt?.content ?? '').trim();
  const isContentDirty = trimmedContent !== originalTrimmed;
  const canSave = trimmedContent.length > 0 && isContentDirty && !isSaving && !readOnly;

  const handleSavePrompt = async () => {
    if (!canSave) return;

    setIsSaving(true);
    try {
      if (activePrompt) {
        await updateActivePromptContent(promptContent);
        toast.success('Prompt saved successfully!');
      } else {
        await createAndActivatePrompt({
          name: `Initial Prompt for Agent ${agentId.substring(0, 8)}`,
          content: promptContent,
        });
        toast.success('New prompt created and saved!');
      }
    } catch (e: any) {
      toast.error(`Failed to save prompt: ${e.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevertToVersion = async (promptId: string) => {
    if (readOnly) return;
    try {
      await setActivePrompt(promptId);
      toast.success('Prompt reverted successfully!');
      setIsHistoryModalOpen(false);
    } catch (e: any) {
      toast.error(`Failed to revert: ${e.message}`);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center h-64">
          <Spinner text="Loading prompt..." />
        </div>
      );
    }
    if (error) {
      return <Alert type="error" title="Error Loading Prompt" message={error.message} />;
    }

    return (
      <TextareaField
        label="Agent's core instructions"
        value={promptContent}
        onChange={(e) => setPromptContent(e.target.value)}
        rows={15}
        applyCustomScrollbar
        placeholder="You are a helpful and friendly AI assistant..."
        disabled={readOnly}
      />
    );
  };

  const historyItems = prompts ?? [];
  const hasRealHistory = historyItems.length > 1;

  return (
    <>
      <Card
        title="System Prompt"
        icon={<Brain size={18} />}
        headerBgColor="bg-violet-100"
        actionButton={
          <div className="flex items-center gap-2">
            {readOnly ? (
              <Tooltip text="View-only">
                <Lock size={16} className="text-slate-500" />
              </Tooltip>
            ) : (
              <IconButton
                icon={<History size={16} />}
                ariaLabel="View prompt history"
                variant="ghost"
                onClick={() => setIsHistoryModalOpen(true)}
                tooltip="View past versions"
                disabled={!historyItems.length}
              />
            )}
            {!readOnly && canSave && (
              <Button
                text={isSaving ? 'Saving…' : 'Save'}
                icon={<Save size={16} />}
                onClick={handleSavePrompt}
                disabled={!canSave}
              />
            )}
          </div>
        }
      >
        {renderContent()}
      </Card>

      <Modal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        title="Prompt History"
        size="lg"
      >
        <div className="max-h-[60vh] overflow-y-auto custom-scrollbar pr-2 space-y-3">
          {historyItems.length === 0 ? (
            <Alert message="No prompt history found." type="info" />
          ) : !hasRealHistory ? (
            <Alert
              type="info"
              message="Prompt history will appear here once additional versions are saved."
            />
          ) : (
            historyItems.map((p: PromptResponse) => (
              <div
                key={p.id}
                className={`p-4 rounded-lg border-2 border-black ${
                  p.id === activePrompt?.id ? 'bg-emerald-50' : 'bg-slate-50'
                }`}
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500 font-medium">
                      Saved: {new Date(p.created_at).toLocaleString()}
                    </p>
                    <p className="text-sm text-slate-700 mt-1 line-clamp-2">{p.content}</p>
                  </div>
                  {p.id === activePrompt?.id ? (
                    <div className="flex items-center gap-2 text-emerald-600 font-semibold text-sm mt-1 shrink-0">
                      <CheckCircle size={16} /> Active
                    </div>
                  ) : readOnly ? (
                    <Tooltip text="View-only">
                      <Lock size={16} className="text-slate-500" />
                    </Tooltip>
                  ) : (
                    <Button
                      text="Revert"
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRevertToVersion(p.id)}
                      className="shrink-0"
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </Modal>
    </>
  );
};